import { isBot } from '../utils/botDetection'

/** How long to wait for OneTrust before initialising monitoring regardless. */
const ONETRUST_WAIT_MS = 5000

/**
 * Nuxt plugin for Datadog Real User Monitoring (RUM) and Logging integration.
 *
 * This plugin:
 * 1. Skips initialization for federated and development environments
 * 2. Waits for OneTrust to initialise before starting (its cookies are strictly
 *    necessary, category C0001), with a bounded fallback if OneTrust never turns up
 * 3. Detects bot traffic and adjusts sampling rate
 * 4. Initializes Datadog RUM and Logging with custom configuration
 *
 * @type {import('nuxt/app').NuxtPlugin}
 * @name datadog
 * @see https://docs.datadoghq.com/getting_started/site/
 */
export default defineNuxtPlugin({
  name: 'datadog',
  parallel: true,
  setup() {
    const config = useRuntimeConfig()

    // Skip initialization for federated and development environments
    if (config.public.isFederated) return
    if (import.meta.dev) return

    // Datadog's session cookie is strictly necessary (C0001):
    // - initialize Datadog after OneTrust has loaded
    // - 5s timeout in case OneTrust never loads for any reason
    let started = false
    const start = () => {
      if (started) return
      started = true
      clearTimeout(fallback)
      void initMonitoring(config)
    }
    const fallback = setTimeout(start, ONETRUST_WAIT_MS)
    useUserCookieConsent({ categories: ['C0001'], onFirstAccept: start })
  },
})

/**
 * Loads and initialises RUM and Logs. Runs after the plugin has returned, outside the
 * Nuxt context, which is why the runtime config is passed in rather than read here.
 */
async function initMonitoring(config: ReturnType<typeof useRuntimeConfig>) {
  // Monitoring must never be able to take the page down. When this ran inside the
  // plugin, a rejection was collected by Nuxt and rethrown at the end of the plugin
  // phase, which set payload.error; the root component then rendered the error branch
  // instead of the app, discarding the markup the server had already produced. That
  // is the empty document a crawler reports. It now runs detached, so a throw would
  // be an unhandled rejection instead - still not something monitoring should cause.
  //
  // Both the promise and the resolved value are checked. On the observed
  // failure the promise had FULFILLED with `undefined`, so the throw came from
  // the destructuring and a try/catch alone would not have caught it. Why it
  // fulfilled empty is not established - a failed chunk fetch normally rejects,
  // so a circular chunk around the shared browser-core module is the likelier
  // explanation - and checking the value covers both cases without having to
  // settle that question.
  //
  // The two are initialised independently. They ship as separate chunks, and on
  // the observed failure RUM had loaded while Logs had not: losing one is not a
  // reason to give up the other.
  const [rum, logs] = await Promise.all([
    import('@datadog/browser-rum').catch(() => undefined),
    import('@datadog/browser-logs').catch(() => undefined),
  ])

  const datadogRum = rum?.datadogRum
  const datadogLogs = logs?.datadogLogs

  if (!datadogRum && !datadogLogs) {
    console.error(
      '[datadog] monitoring could not be loaded; continuing without it'
    )
    return
  }

  // Wrapped as well: a missing runtime config value would throw just as
  // effectively as a missing chunk did, and the goal is that nothing about
  // monitoring can break the page.
  try {
    // Bots get no sampling; real users get the configured rate.
    const sampleRate = isBot(navigator.userAgent)
      ? 0
      : Number(config.public.datadog.ddSessionSampleRate)

    if (datadogRum) {
      datadogRum.init({
        applicationId: config.public.datadog.ddApplicationId,
        clientToken: config.public.datadog.ddClientToken,
        // `site` refers to the Datadog site parameter of your organization
        // see https://docs.datadoghq.com/getting_started/site/
        site: 'datadoghq.eu',
        service: config.public.datadog.ddService,
        env: config.public.datadog.ddEnv,
        version: config.public.app_version as string,
        sessionSampleRate: sampleRate,
        sessionReplaySampleRate: 100,
        trackUserInteractions: true,
        trackResources: true,
        trackLongTasks: true,
        defaultPrivacyLevel: 'mask-user-input',
      })
    }

    if (datadogLogs) {
      datadogLogs.init({
        clientToken: config.public.datadog.ddClientToken,
        site: 'datadoghq.eu',
        forwardErrorsToLogs: true,
        forwardConsoleLogs: ['error'],
        service: config.public.datadog.ddService,
        sessionSampleRate: sampleRate,
        version: config.public.app_version as string,
        env: config.public.datadog.ddEnv,
      })
    }
  } catch (error) {
    console.error(
      '[datadog] monitoring could not be initialised; continuing without it',
      error
    )
  }
}
